/**
 * XML Format Tool
 *
 * Format/prettify XML with configurable indentation.
 */

import { XMLBuilder,XMLParser } from 'fast-xml-parser';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const XmlFormatTool: ITransformerTool = {
  id: 'xml-format',
  name: 'XML Format',
  description: 'Format and prettify XML with configurable indentation',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['xml', 'format', 'beautify', 'prettify', 'indent'],

  seo: {
    slug: 'xml-formatter',
    pageTitle: 'XML Formatter - Free Online Tool | SWE DevTools',
    metaDescription: 'Format and prettify XML with configurable indentation. Clean up messy XML instantly. Fast, private, runs entirely in your browser.',
    keywords: ['xml formatter', 'xml beautifier', 'format xml online', 'xml prettify', 'xml indent'],
    relatedTools: ['xml-minify', 'xml-validate', 'xml-to-json'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.XML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.XML,

  attributes: [
    {
      name: 'indentSize',
      type: 'number',
      label: 'Indent Size',
      defaultValue: 2,
      description: 'Number of spaces per indentation level',
      validation: { min: 1, max: 8, step: 1 },
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const indentSize = typeof config.indentSize === 'number' ? config.indentSize : 2;
    const text = String(input ?? '').trim();
    if (!text) return '';

    try {
      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: '@_',
        textNodeName: '#text',
        allowBooleanAttributes: true,
        preserveOrder: true,
        commentPropName: '#comment',
        cdataPropName: '#cdata',
        processEntities: false,
      });

      const parsed = parser.parse(text);

      const builder = new XMLBuilder({
        ignoreAttributes: false,
        attributeNamePrefix: '@_',
        textNodeName: '#text',
        format: true,
        indentBy: ' '.repeat(indentSize),
        preserveOrder: true,
        commentPropName: '#comment',
        cdataPropName: '#cdata',
        processEntities: false,
        suppressEmptyNode: false,
      });

      const result = builder.build(parsed) as string;
      return result.trimEnd();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to format XML';
      throw ToolError.execution(message);
    }
  },
};
